import { deleteAuthorizationHeader, getResData, httpClient } from "utils";

import { DOCUMENTS_API_PATHS } from "./consts";
import {
  BulkIngestResponse,
  CreateBillInDocumentDTO,
  DocumentByIdDTO,
  DocumentSupportingDocument,
  DocumentsDTO,
  DocumentsQuery,
  PDFDocumentToS3,
} from "./types";

export const createDocuments = ({
  fileNames,
  siteId,
}: {
  fileNames: string[];
  siteId: number;
}) =>
  httpClient
    .post(
      DOCUMENTS_API_PATHS.DOCUMENTS(),
      fileNames.map((filename) => ({ filename, siteId })),
    )
    .then(getResData);

export const createCommonDocuments = ({
  filename,
  billGuid,
}: {
  filename: string;
  billGuid: string;
}) =>
  httpClient
    .post(DOCUMENTS_API_PATHS.COMMON_DOCUMENTS(), { filename, billGuid })
    .then(getResData);

export const documentUploaded = (document: PDFDocumentToS3) =>
  httpClient.post(DOCUMENTS_API_PATHS.DOCUMENTS_UPLOADED, [document]);

export const commonDocumentUploaded = (documentUuid: string) =>
  httpClient.post(DOCUMENTS_API_PATHS.COMMON_DOCUMENTS_UPLOADED, {
    documentUuid,
  });

export const uploadDocumentToS3 = ({
  url,
  file,
}: {
  url: string;
  file: File;
}) =>
  httpClient.put(url, file, {
    headers: {
      "Content-Type": file.type,
    },
    transformRequest: deleteAuthorizationHeader,
  });

export const getDocuments = (query: DocumentsQuery): Promise<DocumentsDTO> =>
  httpClient
    .get<DocumentsDTO>(DOCUMENTS_API_PATHS.DOCUMENTS(query))
    .then(getResData);

export const deleteDocument = (documentId: string) =>
  httpClient.delete(DOCUMENTS_API_PATHS.DOCUMENT(documentId));

export const downloadDocuments = (documentIds: string[]) =>
  httpClient.post(DOCUMENTS_API_PATHS.EXPORT_DOCUMENTS, documentIds, {
    responseType: "blob",
  });

export const getDocumentById = (documentId: string) =>
  httpClient
    .get<DocumentByIdDTO>(DOCUMENTS_API_PATHS.DOCUMENT(documentId))
    .then(getResData);

export const createBillInDocument = ({
  documentId,
  body,
}: {
  documentId: string;
  body: CreateBillInDocumentDTO;
}) =>
  httpClient
    .post(DOCUMENTS_API_PATHS.CREATE_BILL(documentId), body)
    .then(getResData);

export const getDocumentBills = (documentId: string) =>
  httpClient
    .get<any>(DOCUMENTS_API_PATHS.GET_BILLS(documentId))
    .then(getResData);

export const getDocumentSupportingDocuments = (
  guid: string,
): Promise<DocumentSupportingDocument[]> =>
  httpClient
    .get<DocumentSupportingDocument[]>(
      DOCUMENTS_API_PATHS.SUPPORTING_DOCUMENTS_BY_GUID(guid),
    )
    .then(getResData);

export const uploadDocumentSupportingDocument = (
  guid: string,
  file: File,
): Promise<DocumentSupportingDocument[]> => {
  const formData = new FormData();
  formData.append(
    "documentFiles",
    new Blob([JSON.stringify([{ guid, fileIndices: [0] }])], { type: "application/json" }),
  );
  formData.append("files", file);
  return httpClient
    .post<DocumentSupportingDocument[]>(
      DOCUMENTS_API_PATHS.SUPPORTING_DOCUMENTS,
      formData,
    )
    .then(getResData);
};

export const deleteDocumentSupportingDocument = (id: number): Promise<void> =>
  httpClient.delete(DOCUMENTS_API_PATHS.SUPPORTING_DOCUMENT(id));

export const bulkIngestDocuments = (
  siteId: number,
  files: File[],
  overrideFlag = false,
): Promise<BulkIngestResponse> => {
  const formData = new FormData();
  formData.append("siteId", String(siteId));
  files.forEach((file) => formData.append("files", file));
  formData.append("overrideFlag", String(overrideFlag));
  return httpClient
    .post<BulkIngestResponse>(DOCUMENTS_API_PATHS.BULK_INGEST, formData)
    .then(getResData);
};
